// MaturaQuadraticSequenceS3ClosedTask.js
// Zadanie maturalne – zamknięte (ciąg kwadratowy):
// Suma S_m pierwszych m wyrazów ciągu (a_n) = k n^2 + l n, m = 3,4 lub 5.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

function randInt(min, max) {
  const span = max - min + 1;
  return Math.floor(Math.random() * span) + min;
}

function randNonZeroInt(min, max) {
  let v;
  do {
    v = randInt(min, max);
  } while (v === 0);
  return v;
}

function shuffle(array) {
  const a = [...array];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/* =============================== pomocnicze =============================== */

// ładny zapis a_n = k n^2 + l n, bez „1n²”, „+ 1n”, „- 1n”
function buildAnLatex(k, l) {
  let leading;
  if (k === 1) leading = "n^{2}";
  else if (k === -1) leading = "-n^{2}";
  else leading = `${k}n^{2}`;

  let tail = "";
  if (l !== 0) {
    const sign = l < 0 ? "-" : "+";
    const absL = Math.abs(l);
    const coeff = absL === 1 ? "" : String(absL);
    tail = ` ${sign} ${coeff}n`;
  }
  return leading + tail;
}

/* =============================== GENERATOR =============================== */

/**
 * Wariant „oryginał” – matura podstawowa, czerwiec 2025, zadanie 14.1:
 *   a_n = 3n^2 - 3n;  S_3 = 0 + 6 + 18 = 24  (B).
 */
function createJune2025OriginalTask() {
  const options = [
    { value: 18, latex: "18", label: "A" },
    { value: 24, latex: "24", label: "B" },
    { value: 60, latex: "60", label: "C" },
    { value: 90, latex: "90", label: "D" },
  ];

  return {
    type: "MaturaQuadraticSequenceS3ClosedTask",
    data: { k: 3, l: -3, m: 3, Sm: 24, options, correctIndex: 1, maxPoints: 1 },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_CZE",
      taskNumber: "14.1",
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_CZE"
  ) {
    return createJune2025OriginalTask();
  }

  // k ∈ {1,…,5}, l ∈ {-5,…,-1,1,…,5}, bez zera
  const k = randNonZeroInt(1, 5);
  const l = randNonZeroInt(-5, 5);

  // liczba początkowych wyrazów: 3, 4 albo 5
  const m = [3, 4, 5][randInt(0, 2)];

  // wyrazy ciągu a_n = k n^2 + l n dla n = 1..m+1
  const terms = [];
  for (let n = 1; n <= m + 1; n += 1) {
    terms.push(k * n * n + l * n);
  }

  const sumFirst = (count, offset = 0) => {
    let s = 0;
    for (let i = 0; i < count; i += 1) {
      s += terms[offset + i];
    }
    return s;
  };

  const Sm = sumFirst(m); // poprawna suma
  const a_m = terms[m - 1]; // pojedynczy m-ty wyraz – klasyczny dystraktor

  // kandydaci na błędne odpowiedzi (jak na maturze)
  const candidateSet = new Set();
  candidateSet.add(a_m); // sam m-ty wyraz
  candidateSet.add(sumFirst(m - 1)); // S_{m-1}
  candidateSet.add(sumFirst(m + 1)); // S_{m+1}
  candidateSet.add(sumFirst(m, 1)); // suma a_2 + ... + a_{m+1}

  let wrong = Array.from(candidateSet).filter((v) => v !== Sm);

  // jeżeli coś się zlało, dopchniemy lekkimi perturbacjami
  while (wrong.length < 3) {
    const delta = randInt(-3, 3) * k;
    if (delta === 0) continue;
    const cand = Sm + delta;
    if (cand !== Sm && !wrong.includes(cand)) wrong.push(cand);
  }

  wrong = shuffle(wrong).slice(0, 3);

  const allValues = shuffle([Sm, ...wrong]);
  const correctIndex = allValues.indexOf(Sm);

  const options = allValues.map((value, i) => ({
    value,
    latex: String(value),
    label: LETTERS[i],
  }));

  return {
    type: "MaturaQuadraticSequenceS3ClosedTask",
    data: {
      k,
      l,
      m,
      Sm,
      options,
      correctIndex,
      maxPoints: 1,
    },
  };
}

/* =============================== TREŚĆ ==================================== */

export function renderTask({ data }) {
  const { k, l, m, options } = data;

  const anLatex = buildAnLatex(k, l);
  const mLatex = String(m);

  return (
    <div style={{ textAlign: "left" }} className="space-y-2">
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>
      <p>
        Ciąg <InlineMath math={'(a_n)'} /> jest określony wzorem{" "}
        <InlineMath math={`a_n = ${anLatex}`} /> dla każdej liczby naturalnej{" "}
        <InlineMath math={'n \\ge 1'} />.
      </p>

      <p>
        Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.
      </p>

      <p>
        Suma <InlineMath math={`S_{${mLatex}}`} />{" "}
        {m === 3
          ? "trzech"
          : m === 4
          ? "czterech"
          : "pięciu"}{" "}
        początkowych kolejnych wyrazów ciągu{" "}
        <InlineMath math={'(a_n)'} /> jest równa:
      </p>

      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 10 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* =============================== INPUT (ABCD) ============================= */

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;

  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
      value: opt.value,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

/* =============================== PODGLĄD ODP. ============================ */

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    userAnswer.latex == null
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;

  return (
    <div style={{ marginTop: "1rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}

/* =============================== SPRAWDZANIE ============================= */

export function checkAnswer({ data }, userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !Number.isInteger(userAnswer.choiceIndex)
  ) {
    return false;
  }
  return userAnswer.choiceIndex === data.correctIndex;
}

/* =============================== POPRAWNA ODP. =========================== */

export function renderAnswer({ data }) {
  const { options, correctIndex } = data;
  const correct = options[correctIndex];

  // dokładnie ten sam format jak w poleceniu: „A. 170”
  return <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />;
}


/* =============================== WYJAŚNIENIE ============================= */

export function renderExplanation({ data }) {
  const { k, l, m, Sm } = data;

  const anLatex = buildAnLatex(k, l);
  const mLatex = String(m);
  const indicesLatex = Array.from({ length: m }, (_, index) => String(index + 1)).join(",");
  const symbolicSumLatex = Array.from(
    { length: m },
    (_, index) => `a_{${index + 1}}`
  ).join(" + ");

  const kLatex = k < 0 ? `(${k})` : String(k);

  const values = [];
  const aLines = [];

  for (let n = 1; n <= m; n += 1) {
    const an = k * n * n + l * n;
    values.push(an);

    let rhs = `${kLatex}\\cdot ${n}^2`;
    if (l !== 0) {
      const sign = l < 0 ? " - " : " + ";
      const absL = Math.abs(l);
      rhs += `${sign}${absL}\\cdot ${n}`;
    }

    aLines.push(`a_{${n}} = ${rhs} = ${an}`);
  }

  const sumNums = values.map(value => value < 0 ? `(${value})` : String(value)).join(" + ");

  return (
    <div style={{ textAlign: "left" }} className="space-y-2">
      <p>
        <strong>Krok 1.</strong> Zapisujemy wzór na wyraz ogólny ciągu{" "}
        <InlineMath math={'(a_n)'} /> i obliczamy kolejne wyrazy dla{" "}
        <InlineMath math={`n = ${indicesLatex}`} />:
      </p>
      <BlockMath math={`a_n = ${anLatex}`} />
      {aLines.map((line) => (
        <BlockMath key={line} math={line} />
      ))}

      <p>
        <strong>Krok 2.</strong> Suma{" "}
        <InlineMath math={`S_{${mLatex}}`} /> to suma tych{" "}
        {m === 3 ? "trzech" : m === 4 ? "czterech" : "pięciu"} wyrazów:
      </p>
      <BlockMath
        math={`S_{${mLatex}} = ${symbolicSumLatex}`}
      />
      <BlockMath
        math={`S_{${mLatex}} = ${sumNums} = ${Sm}.`}
      />
      <p>
        Dlatego poprawna odpowiedź to liczba{" "}
        <InlineMath math={String(Sm)} />.
      </p>
    </div>
  );
}

/* =============================== EXPORT DOMYŚLNY ========================= */

export default {
  generateTask,
  renderTask,
  renderInput,
  renderUserAnswer,
  checkAnswer,
  renderAnswer,
  renderExplanation,
};
